import type { QuizAddonQuestion, QuizSavedAnswer } from './Quiz';

/**
 * Map a quiz score to the suffix of its result-message i18n key
 * (`experienceV2.quiz.message.<suffix>`), banded by the share answered
 * correctly.
 */
export const scoreMessageKey = (correct: number, total: number): string => {
  const ratio = total === 0 ? 0 : correct / total;
  if (ratio === 1) return 'perfect';
  if (ratio >= 0.8) return 'great';
  if (ratio >= 0.6) return 'good';
  if (ratio >= 0.4) return 'ok';
  return 'low';
};

/** A question drawn for an attempt, tagged with its index in the pool. */
export type DrawnQuestion = { question: QuizAddonQuestion; poolIndex: number };

/** How many questions one attempt serves: the whole pool when `n` is unset or
 *  not smaller. */
export const attemptSize = (poolSize: number, n?: number): number =>
  !n || n < 0 || n >= poolSize ? poolSize : n;

/** Fisher–Yates shuffle, in place. */
export const shuffle = <T>(items: T[]): T[] => {
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = items[i];
    const b = items[j];
    if (a === undefined || b === undefined) continue;
    items[i] = b;
    items[j] = a;
  }
  return items;
};

/** A fresh draw of `n` questions from the pool, after the already `answered`
 *  ones of a resumed attempt. The rest of the pool, in order, when `n` is unset
 *  or not smaller; else a random pick. */
export const sampleQuestions = (
  pool: QuizAddonQuestion[],
  n?: number,
  answered: DrawnQuestion[] = [],
): DrawnQuestion[] => {
  const size = attemptSize(pool.length, n);
  const taken = new Set(answered.map((d) => d.poolIndex));
  const rest = pool
    .map((question, poolIndex) => ({ question, poolIndex }))
    .filter((d) => !taken.has(d.poolIndex));
  if (size === pool.length) return [...answered, ...rest];
  return [...answered, ...shuffle(rest).slice(0, size - answered.length)];
};

/**
 * Rebuilds a saved attempt: the answered questions first, in order, then the
 * rest drawn from the pool minus those. Undefined (start fresh) when nothing
 * was answered or the saved answers don't fit the pool or the attempt size.
 */
export const restoreAttempt = (
  pool: QuizAddonQuestion[],
  n: number | undefined,
  saved: QuizSavedAnswer[] | undefined,
): { drawn: DrawnQuestion[]; chosen: Record<number, number> } | undefined => {
  if (!saved || saved.length === 0) return undefined;
  if (saved.length > attemptSize(pool.length, n)) return undefined;

  const answered: DrawnQuestion[] = [];
  const chosen: Record<number, number> = {};
  const seen = new Set<number>();
  for (const [i, { poolIndex, answerIndex }] of saved.entries()) {
    const question = pool[poolIndex];
    if (
      question === undefined ||
      seen.has(poolIndex) ||
      !Number.isInteger(answerIndex) ||
      answerIndex < 0 ||
      answerIndex >= question.answers.length
    )
      return undefined;
    seen.add(poolIndex);
    answered.push({ question, poolIndex });
    chosen[i] = answerIndex;
  }

  return { drawn: sampleQuestions(pool, n, answered), chosen };
};

/** The attempt's answered questions, in order, by pool position. */
export const toSavedAnswers = (
  drawn: DrawnQuestion[],
  picks: Record<number, number>,
): QuizSavedAnswer[] =>
  drawn.flatMap((d, i) => {
    const answerIndex = picks[i];
    return answerIndex === undefined
      ? []
      : [{ poolIndex: d.poolIndex, answerIndex }];
  });
